import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FilePlus2, Info, X } from 'lucide-react';
import AccessibleModal from '../../components/common/AccessibleModal';
import { accountStrings } from '../../content/account';
import RatingReadout from '../../components/common/RatingReadout';
import HobtalkLoader from '../../components/common/HobtalkLoader';
import { mediaStrings, moduleCommonStrings } from '../../content';
import { getPublicErrorMessage } from '../../utils/errors';
import { includesCollectionName, normalizeCollectionOptions } from '../../utils/collectionOptions';

/** Reads the selected Goodreads CSV before requesting an import preview. */
function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error(mediaStrings.reading.import.fileReadError));
    reader.readAsText(file);
  });
}

/** Lets readers upload, review, and selectively import a Goodreads export. */
export default function GoodreadsImportModal({
  error, importResult, isImporting, isPreviewing, onClose, onImport, onPreview, onUpgrade, preview, remainingEntries = null, collections = [],
  usageReady = true, usageError = false, onRetryUsage,
}) {
  const inputRef = useRef(null);
  const [csvText, setCsvText] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileError, setFileError] = useState('');
  const [collectionError, setCollectionError] = useState('');
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [importedRowIds, setImportedRowIds] = useState([]);
  const [visibleRowCount, setVisibleRowCount] = useState(50);
  const collectionOptions = useMemo(() => normalizeCollectionOptions(collections), [collections]);
  const [collectionMode, setCollectionMode] = useState(collectionOptions.length ? 'existing' : 'new');
  const [collection, setCollection] = useState(collectionOptions[0] || 'My Books');

  const visiblePreview = csvText && !fileError ? preview : null;
  const previewItems = (visiblePreview?.items || []).map((item) => importedRowIds.includes(item.rowId)
    ? { ...item, duplicate: true, duplicateReason: moduleCommonStrings.imports.alreadyImported } : item);
  const importableRowIds = previewItems.filter((item) => !item.duplicate).map((item) => item.rowId);
  const selectionLimit = usageReady ? Math.max(0, Math.min(visiblePreview?.maxBatchSize ?? 25, remainingEntries ?? Infinity)) : 0;
  const showProUpsell = usageReady && remainingEntries != null && remainingEntries < Math.min(importableRowIds.length, visiblePreview?.maxBatchSize ?? 25);
  const selectableRowIds = importableRowIds.slice(0, selectionLimit);
  const allImportableSelected = selectableRowIds.length > 0 && selectableRowIds.every((id) => selectedRowIds.includes(id));

  useEffect(() => {
    if (!usageReady) return;
    setSelectedRowIds((current) => current.length > selectionLimit ? current.slice(0, selectionLimit) : current);
  }, [selectionLimit, usageReady]);

  /** Replaces the draft file, then selects every importable preview row. */
  const handleFileChange = async (event) => {
    if (!usageReady) return;
    const file = event.target.files?.[0];
    setFileError('');
    setCsvText('');
    setFileName('');
    setSelectedRowIds([]);
    setImportedRowIds([]);
    setVisibleRowCount(50);
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setFileError(mediaStrings.reading.import.invalidFile);
      event.target.value = '';
      return;
    }

    try {
      const text = await readFileAsText(file);
      const nextPreview = await onPreview(text);
      if (!nextPreview) throw new Error(mediaStrings.reading.import.fileReadError);
      setCsvText(text);
      setFileName(file.name);
      setSelectedRowIds((nextPreview?.items || []).filter((item) => !item.duplicate)
        .slice(0, Math.min(nextPreview?.maxBatchSize ?? 25, remainingEntries ?? Infinity)).map((item) => item.rowId));
    } catch (readError) {
      setFileError(getPublicErrorMessage(readError, mediaStrings.reading.import.fileReadError));
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  /** Toggles one book in the local import selection. */
  const toggleRow = (rowId) => {
    setSelectedRowIds((current) => current.includes(rowId)
      ? current.filter((id) => id !== rowId)
      : current.length < selectionLimit ? [...current, rowId] : current);
  };

  /** Imports only selected rows into the chosen Reading collection. */
  const handleImport = async () => {
    if (!usageReady || !csvText || !visiblePreview || selectedRowIds.length === 0 || selectedRowIds.length > selectionLimit) return;
    if (collectionMode === 'new' && includesCollectionName(collectionOptions, collection)) {
      setCollectionError(mediaStrings.reading.form.errors.collectionDuplicate);
      return;
    }
    const didImport = await onImport({ csvText, selectedRowIds, collection: collection.trim() || 'My Books' });
    if (didImport) {
      const failed = new Set(didImport.failures?.map((item) => item.rowId) || []);
      setImportedRowIds((current) => [...current, ...selectedRowIds.filter((id) => !failed.has(id))]);
      setSelectedRowIds([]);
    }
  };

  return (
    <AccessibleModal className="import-flow catalog-import-modal" overlayClassName="catalog-import-overlay" labelledBy="goodreads-import-title" onClose={onClose}>
      {(isPreviewing || isImporting) && (
        <HobtalkLoader variant="overlay" label={isImporting ? mediaStrings.reading.import.loading.importing(selectedRowIds.length) : mediaStrings.reading.import.loading.previewing} />
      )}

      <header className="catalog-import-header">
        <h2 id="goodreads-import-title">{mediaStrings.reading.import.title}</h2>
        <button type="button" className="btn-ghost icon-button" onClick={onClose} aria-label={moduleCommonStrings.actions.close} title={moduleCommonStrings.actions.close}><X size={18} /></button>
      </header>

      {!usageReady && <div className="catalog-import-usage-state" role={usageError ? 'alert' : 'status'}>
        <span>{usageError ? accountStrings.subscription.loadFailed : accountStrings.subscription.loading}</span>
        {usageError && <button type="button" className="inline-link" onClick={onRetryUsage}>{moduleCommonStrings.imports.retry}</button>}
      </div>}

      {!visiblePreview && <section className="catalog-import-section">
        <h3>{mediaStrings.reading.import.exportTitle}</h3>
        <p>{mediaStrings.reading.import.exportDetail}</p>
        <a className="inline-link" href="https://www.goodreads.com/review/import" target="_blank" rel="noreferrer">{mediaStrings.reading.import.getCsvAction}</a>
        <button type="button" className="catalog-file-picker" disabled={!usageReady} onClick={() => inputRef.current?.click()}><FilePlus2 size={18} aria-hidden="true" />{fileName || mediaStrings.reading.import.filePicker}</button>
        <input ref={inputRef} type="file" accept=".csv,text/csv" onChange={handleFileChange} hidden />
      </section>
      }
      {(fileError || error) && <div role="alert" className="catalog-import-error"><p className="error-copy">{fileError || getPublicErrorMessage(error)}</p><p>{moduleCommonStrings.imports.fileErrorHelp}</p></div>}
      {visiblePreview && showProUpsell && <div className="catalog-import-upsell">
        <div><strong>{moduleCommonStrings.imports.upsellTitle}</strong><p>{moduleCommonStrings.imports.upsellDetail}</p></div>
        <button type="button" onClick={onUpgrade}>{moduleCommonStrings.imports.moreWithPro}</button>
      </div>}

      {visiblePreview && (
        <section className="catalog-import-section catalog-review-section">
          <div className="catalog-review-heading">
            <h3>{mediaStrings.reading.import.reviewTitle}</h3>
            <details className="catalog-import-info"><summary aria-label="Import batch limit" title="Import batch limit"><Info size={16} /></summary><span>{moduleCommonStrings.imports.batchLimit(visiblePreview.maxBatchSize ?? 25)}</span></details>
            <button type="button" className="inline-link catalog-change-file" disabled={!usageReady} title={fileName} onClick={() => inputRef.current?.click()}>{moduleCommonStrings.imports.changeFile}</button>
            <input ref={inputRef} type="file" accept=".csv,text/csv" onChange={handleFileChange} hidden />
          </div>
          <div className="catalog-import-metrics">
            <div><strong>{visiblePreview.totalRows}</strong><span>{mediaStrings.reading.import.rows}</span></div>
            <div><strong>{previewItems.filter((item) => !item.duplicate).length}</strong><span>{mediaStrings.reading.import.ready}</span></div>
            <div><strong>{previewItems.filter((item) => item.duplicate).length}</strong><span>{mediaStrings.reading.import.duplicates}</span></div>
          </div>
          {previewItems.length === 0 && <p className="catalog-import-empty">{moduleCommonStrings.imports.noItems}</p>}
          {previewItems.length > 0 && importableRowIds.length === 0 && <p className="catalog-import-empty">{moduleCommonStrings.imports.allDuplicates}</p>}

          {importableRowIds.length > 0 && <>
          <div className="goodreads-collection-field">
            <label>{mediaStrings.reading.import.collection}</label>
            <div className="collection-picker">
              {collectionOptions.length > 0 && (
                <div className="collection-mode-toggle" role="group" aria-label={mediaStrings.reading.import.collectionMode}>
                  <button type="button" className={collectionMode === 'existing' ? 'active' : ''} onClick={() => { setCollectionMode('existing'); setCollection(collectionOptions[0]); setCollectionError(''); }}>{mediaStrings.reading.import.existing}</button>
                  <button type="button" className={collectionMode === 'new' ? 'active' : ''} onClick={() => { setCollectionMode('new'); setCollection(''); setCollectionError(''); }}>{mediaStrings.reading.import.createNew}</button>
                </div>
              )}
              {collectionMode === 'existing' && collectionOptions.length > 0 ? (
                <select value={collectionOptions.includes(collection) ? collection : collectionOptions[0]} onChange={(event) => setCollection(event.target.value)}>
                  {collectionOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              ) : (
                <input value={collection} onChange={(event) => { setCollection(event.target.value); setCollectionError(''); }} placeholder={mediaStrings.reading.import.collectionPlaceholder} />
              )}
            </div>
            {collectionError && <p className="field-error">{collectionError}</p>}
          </div>

          <button type="button" className="catalog-selection-toggle" onClick={() => setSelectedRowIds(allImportableSelected ? [] : selectableRowIds)} disabled={selectionLimit === 0}>
            <strong>{allImportableSelected ? mediaStrings.reading.import.clearSelection : mediaStrings.reading.import.selectImportable}</strong>
            <span>{mediaStrings.reading.import.selected(selectedRowIds.length)}</span>
          </button>

          <div className="goodreads-import-list">
            {previewItems.slice(0, visibleRowCount).map((item) => (
              <label key={item.rowId} className={`goodreads-import-row ${item.duplicate ? 'duplicate' : ''}`}>
                <input type="checkbox" checked={selectedRowIds.includes(item.rowId)} disabled={item.duplicate || (!selectedRowIds.includes(item.rowId) && selectedRowIds.length >= selectionLimit)} onChange={() => toggleRow(item.rowId)} />
                <span>
                  <strong>{item.book.title || mediaStrings.reading.import.untitledBook}</strong>
                  <em>{[item.book.authors?.join(', '), item.book.shelf].filter(Boolean).join(' • ')}</em>
                  {Number(item.book.rating) > 0 && <RatingReadout rating={item.book.rating} />}
                  {item.duplicate && <small>{item.duplicateReason}</small>}
                </span>
              </label>
            ))}
          </div>
          {previewItems.length > visibleRowCount && <button type="button" className="btn-ghost compact-btn" onClick={() => setVisibleRowCount((count) => count + 50)}>{moduleCommonStrings.imports.showMore}</button>}
          </>}
        </section>
      )}

      {importResult && (
        <div className="import-result-card">
          <strong>{mediaStrings.reading.import.importComplete}</strong>
          <p>{mediaStrings.reading.import.result(importResult)}</p>
          {(importResult.failures || []).map((failure) => <p className="error-copy" key={failure.rowId}>{failure.title || mediaStrings.reading.import.bookFallback}: {failure.message}</p>)}
        </div>
      )}

      <div className="form-actions catalog-import-actions">
        <button type="button" className="btn-primary" onClick={handleImport} disabled={!usageReady || !csvText || !visiblePreview || selectedRowIds.length === 0 || selectedRowIds.length > selectionLimit || isImporting || isPreviewing || Boolean(collectionError)}>
          {isImporting ? mediaStrings.reading.import.importing : mediaStrings.reading.import.importAction}
        </button>
      </div>
    </AccessibleModal>
  );
}
